import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Tab, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ComputerIcon from '@mui/icons-material/Computer';
import RouterIcon from '@mui/icons-material/Router';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../accounting/shared/StatsCard';
import accessControlService, {
  AccessAgent, AccessDoor, AccessPanel, AccessProfile, DoorDirection,
} from '../../services/accessControlService';
import AccessProfilesTab from './AccessProfilesTab';
import CardPermissionsTab from './CardPermissionsTab';
import PassLogsTab from './PassLogsTab';
import { ageSinceMs, formatTrDateTime } from './accessTime';

/**
 * Senkron gecikmesi yalnızca GÖRÜNÜRLÜK içindir: uzun süre senkron olmayan bir panel
 * bilgisayarı uyarı çipiyle işaretlenir ama geçişler engellenmez (kapılar son bilinen
 * yetkiyle çalışmaya devam eder).
 */
const SYNC_WARN_AFTER_MS = 10 * 60 * 1000;

const formatCacheAge = (
  lastSyncAt: string | null,
  cm: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
) => {
  const ageMs = ageSinceMs(lastSyncAt);
  if (ageMs === null) return cm('accessManagement.syncNever', 'never synced');

  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return cm('accessManagement.syncJustNow', 'just now');
  if (minutes < 60) return cm('accessManagement.syncMinutes', '{{n}} min ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return cm('accessManagement.syncHours', '{{n}} h ago', { n: hours });
  return cm('accessManagement.syncDays', '{{n}} d ago', { n: Math.floor(hours / 24) });
};

interface DoorDraft {
  name: string;
  direction: '' | 'entry' | 'exit';
  location: string;
}

const toDraft = (door: AccessDoor): DoorDraft => ({
  name: door.name || '',
  direction: (door.direction || '') as DoorDraft['direction'],
  location: door.location || '',
});

const AccessManagement: React.FC = () => {
  const { t } = useTranslation(['cardManagement', 'common2']);
  const cm = useCallback(
    (key: string, defaultValue: string, options?: Record<string, unknown>) =>
      t(key, { ns: 'cardManagement', defaultValue, ...(options || {}) }),
    [t],
  );

  const { userDetails, branches: authBranches, loadBranches } = useAuth();
  const companyId = userDetails?.companyId || '';

  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState<AccessAgent[]>([]);
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [totals, setTotals] = useState({ agents: 0, panels: 0, doors: 0, unconfiguredDoors: 0 });
  const [tablesMissing, setTablesMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingDoorId, setSavingDoorId] = useState<number | null>(null);

  // Kapı satırları yerel taslakta tutulur; yalnızca değişen satır kaydedilir.
  const [doorDrafts, setDoorDrafts] = useState<Record<number, DoorDraft>>({});

  const [pushingAgentId, setPushingAgentId] = useState<number | null>(null);

  const [panelDialog, setPanelDialog] = useState<AccessPanel | null>(null);
  const [panelForm, setPanelForm] = useState({ name: '', location: '', branchId: '', doorCount: 4 });
  const [savingPanel, setSavingPanel] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });

  const showSnackbar = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  const branches = useMemo(() => authBranches.filter((b) => b.id !== 'null'), [authBranches]);

  const loadPanels = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await accessControlService.getPanels();
      const list = res.data?.data || [];
      setAgents(list);
      setTablesMissing(!!res.data?.tablesMissing);
      setTotals(res.data?.totals || { agents: 0, panels: 0, doors: 0, unconfiguredDoors: 0 });

      const drafts: Record<number, DoorDraft> = {};
      list.forEach((agent) => agent.panels.forEach((panel) => panel.doors.forEach((door) => {
        drafts[door.id] = toDraft(door);
      })));
      setDoorDrafts(drafts);
    } catch (error: any) {
      setAgents([]);
      showSnackbar(error?.response?.data?.message || cm('accessManagement.errors.loadFailed', 'Panels could not be loaded.'), 'error');
    }
    setLoading(false);
  }, [companyId]);

  // Profiller üst seviyede tutulur: Profiller sekmesi kapı setini, Kart Yetkileri
  // sekmesi de süzgeç ve atama listesini aynı veriden okur.
  const loadProfiles = useCallback(async () => {
    if (!companyId) return;
    setProfilesLoading(true);
    try {
      const res = await accessControlService.getProfiles();
      setProfiles(res.data?.data || []);
      if (res.data?.tablesMissing) setTablesMissing(true);
    } catch (error: any) {
      setProfiles([]);
      showSnackbar(error?.response?.data?.message || cm('accessManagement.errors.profileLoadFailed', 'Groups could not be loaded.'), 'error');
    }
    setProfilesLoading(false);
  }, [companyId, showSnackbar, cm]);

  useEffect(() => { loadPanels(); }, [loadPanels]);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  useEffect(() => { if (companyId && branches.length === 0) loadBranches(); }, [companyId]);

  const isDoorDirty = (door: AccessDoor) => {
    const draft = doorDrafts[door.id];
    if (!draft) return false;
    const original = toDraft(door);
    return draft.name !== original.name || draft.direction !== original.direction || draft.location !== original.location;
  };

  const updateDraft = (doorId: number, patch: Partial<DoorDraft>) =>
    setDoorDrafts((prev) => ({ ...prev, [doorId]: { ...prev[doorId], ...patch } }));

  const saveDoor = async (door: AccessDoor) => {
    const draft = doorDrafts[door.id];
    if (!draft) return;

    setSavingDoorId(door.id);
    try {
      await accessControlService.updateDoor({
        id: door.id,
        name: draft.name.trim() || null,
        direction: (draft.direction || null) as DoorDirection,
        location: draft.location.trim() || null,
      });
      showSnackbar(cm('accessManagement.doorSaved', 'Door updated.'), 'success');
      await loadPanels();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || cm('accessManagement.errors.doorSaveFailed', 'Door could not be updated.'), 'error');
    }
    setSavingDoorId(null);
  };

  const openPanelDialog = (panel: AccessPanel) => {
    setPanelDialog(panel);
    setPanelForm({
      name: panel.name || '',
      location: panel.location || '',
      branchId: panel.branchId || '',
      doorCount: panel.doorCount || 4,
    });
  };

  const savePanel = async () => {
    if (!panelDialog) return;
    setSavingPanel(true);
    try {
      await accessControlService.updatePanel({
        id: panelDialog.id,
        name: panelForm.name.trim() || null,
        location: panelForm.location.trim() || null,
        branchId: panelForm.branchId || null,
        doorCount: panelForm.doorCount,
      });
      showSnackbar(cm('accessManagement.panelSaved', 'Panel updated.'), 'success');
      setPanelDialog(null);
      await loadPanels();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || cm('accessManagement.errors.panelSaveFailed', 'Panel could not be updated.'), 'error');
    }
    setSavingPanel(false);
  };

  /**
   * Panel bilgisayarını hemen senkrona zorlar. Çevrimdışı yanıt UYARI olarak gösterilir,
   * hata olarak değil: yetki değişikliği kaydedilmiştir ve panel en geç 60 saniyede
   * kendi yoklamasıyla alır.
   */
  const pushSync = async (agent: AccessAgent) => {
    setPushingAgentId(agent.id);
    try {
      const res = await accessControlService.pushSyncToAgent(agent.id);
      if (res.data?.reached) {
        showSnackbar(cm('accessManagement.pushSyncSent', 'Sync trigger sent to the panel computer.'), 'success');
      } else {
        showSnackbar(
          cm('accessManagement.pushSyncOffline', 'Panel computer is offline right now; it will sync itself within 60 seconds at the latest.'),
          'warning',
        );
      }
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message || cm('accessManagement.errors.pushSyncFailed', 'Sync trigger could not be sent.'),
        'error',
      );
    }
    setPushingAgentId(null);
  };

  const renderDoorRow = (door: AccessDoor) => {
    const draft = doorDrafts[door.id] || toDraft(door);
    const dirty = isDoorDirty(door);

    return (
      <Grid container spacing={1.5} alignItems="center" key={door.id} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={12} sm={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MeetingRoomIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="bold">
              {cm('accessManagement.doorNumber', 'Door')} {door.doorNumber}
            </Typography>
            {!door.isActive && (
              <Chip size="small" variant="outlined" label={cm('accessManagement.inactive', 'Passive')} />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={3.5}>
          <TextField
            fullWidth
            size="small"
            label={cm('accessManagement.doorName', 'Door name')}
            value={draft.name}
            onChange={(e) => updateDraft(door.id, { name: e.target.value })}
            placeholder={cm('accessManagement.doorNamePlaceholder', 'e.g. Production Entrance')}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>{cm('accessManagement.direction', 'Direction')}</InputLabel>
            <Select
              value={draft.direction}
              label={cm('accessManagement.direction', 'Direction')}
              onChange={(e) => updateDraft(door.id, { direction: e.target.value as DoorDraft['direction'] })}
            >
              <MenuItem value="">
                <em>{cm('accessManagement.directionUnset', 'Not set')}</em>
              </MenuItem>
              <MenuItem value="entry">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LoginIcon fontSize="small" color="success" />
                  {cm('accessManagement.entry', 'Entry')}
                </Box>
              </MenuItem>
              <MenuItem value="exit">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LogoutIcon fontSize="small" color="warning" />
                  {cm('accessManagement.exit', 'Exit')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label={cm('accessManagement.location', 'Location')}
            value={draft.location}
            onChange={(e) => updateDraft(door.id, { location: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={1.5} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!draft.direction && (
            <Tooltip title={cm('accessManagement.unsetWarning', 'Direction is not set: this door cannot be used in access decisions.')}>
              <WarningAmberIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
          <Tooltip title={cm('accessManagement.save', 'Save')}>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!dirty || savingDoorId === door.id}
                onClick={() => saveDoor(door)}
              >
                {savingDoorId === door.id ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  const renderPanel = (panel: AccessPanel) => (
    <Paper key={panel.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <RouterIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              {panel.name || `${cm('accessManagement.panel', 'Panel')} ${panel.deviceKey.slice(0, 8)}`}
            </Typography>
            <Chip size="small" variant="outlined" label={`${panel.doorCount} ${cm('accessManagement.doors', 'doors')}`} />
            {panel.unconfiguredDoorCount > 0 && (
              <Chip
                size="small"
                color="warning"
                icon={<WarningAmberIcon fontSize="small" />}
                label={`${panel.unconfiguredDoorCount} ${cm('accessManagement.unconfigured', 'unconfigured')}`}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
            {panel.ipAddress || '—'} • {panel.deviceKey}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {panel.location || cm('accessManagement.noLocation', 'No location')} • {cm('accessManagement.lastSeen', 'Last seen')}: {formatTrDateTime(panel.lastSeenAt)}
          </Typography>
        </Box>
        <Button size="small" startIcon={<EditIcon />} onClick={() => openPanelDialog(panel)}>
          {cm('accessManagement.editPanel', 'Edit panel')}
        </Button>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {panel.doors.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {cm('accessManagement.noDoors', 'No doors reported for this panel yet.')}
          </Typography>
        ) : (
          panel.doors.map(renderDoorRow)
        )}
      </Box>
    </Paper>
  );

  const renderAgent = (agent: AccessAgent) => {
    const syncAgeMs = ageSinceMs(agent.lastSyncAt);
    const syncFresh = syncAgeMs !== null && syncAgeMs <= SYNC_WARN_AFTER_MS;

    return (
    <Box key={agent.id} sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <ComputerIcon color="action" />
        <Typography variant="h6" fontWeight="bold">
          {agent.name || cm('accessManagement.unnamedAgent', 'Unnamed panel computer')}
        </Typography>
        <Chip size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} label={agent.screenCode} />
        <Chip
          size="small"
          color={agent.instanceCode ? 'success' : 'default'}
          variant="outlined"
          label={agent.instanceCode
            ? cm('accessManagement.realtimeReady', 'Real-time ready')
            : cm('accessManagement.realtimeMissing', 'No real-time identity')}
        />
        <Tooltip title={cm('accessManagement.syncTooltip', 'Time of the last permission sync. Doors keep working with the last known permissions even if sync is stale.')}>
          <Chip
            size="small"
            variant="outlined"
            icon={syncFresh ? <CloudDoneIcon fontSize="small" /> : <CloudOffIcon fontSize="small" />}
            color={syncFresh ? 'success' : 'warning'}
            label={`${cm('accessManagement.lastSync', 'Sync')}: ${formatCacheAge(agent.lastSyncAt, cm)}`}
          />
        </Tooltip>
        <Tooltip title={cm('accessManagement.pushSyncTooltip', 'Pushes the current permissions to this panel computer immediately instead of waiting for its own 60-second poll.')}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={pushingAgentId === agent.id ? <CircularProgress size={14} /> : <SyncIcon />}
              disabled={pushingAgentId === agent.id || !agent.instanceCode}
              onClick={() => pushSync(agent)}
            >
              {cm('accessManagement.pushSync', 'Send to this computer')}
            </Button>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {cm('accessManagement.lastReport', 'Last report')}: {formatTrDateTime(agent.lastSeenAt)}
        </Typography>
      </Box>
      {agent.panels.length === 0 ? (
        <Alert severity="info">
          {cm('accessManagement.noPanels', 'This computer has not reported any panel yet. Use Options > Access Management in local-service to send the inventory.')}
        </Alert>
      ) : (
        agent.panels.map(renderPanel)
      )}
    </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MeetingRoomIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {cm('accessManagement.title', 'Access Management')}
          </Typography>
        </Box>
        <IconButton onClick={loadPanels} title={cm('common.refresh', 'Refresh')}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <StatsCardsContainer loading={loading && agents.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<ComputerIcon />}
            color={STATS_COLORS.blue}
            title={`${totals.agents}`}
            subtitle={cm('accessManagement.stats.agents', 'Panel computers')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<RouterIcon />}
            color={STATS_COLORS.green}
            title={`${totals.panels}`}
            subtitle={cm('accessManagement.stats.panels', 'Panels')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<MeetingRoomIcon />}
            color={STATS_COLORS.purple}
            title={`${totals.doors}`}
            subtitle={cm('accessManagement.stats.doors', 'Doors')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<WarningAmberIcon />}
            color={STATS_COLORS.orange}
            title={`${totals.unconfiguredDoors}`}
            subtitle={cm('accessManagement.stats.unconfigured', 'Doors without direction')}
          />
        </Grid>
      </StatsCardsContainer>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab label={cm('accessManagement.tabs.passLogs', 'Pass Logs')} />
          <Tab label={cm('accessManagement.tabs.profiles', 'Access Groups')} />
          <Tab label={cm('accessManagement.tabs.cardPermissions', 'Card Permissions')} />
          <Tab label={cm('accessManagement.tabs.doors', 'Doors')} />
        </Tabs>
      </Paper>

      {tablesMissing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {cm('accessManagement.tablesMissing', 'Access management tables have not been created in this company database yet.')}
        </Alert>
      )}

      {tab === 0 && (
        <PassLogsTab agents={agents} notify={showSnackbar} cm={cm} />
      )}

      {tab === 1 && (
        <AccessProfilesTab
          agents={agents}
          profiles={profiles}
          profilesLoading={profilesLoading}
          reloadProfiles={loadProfiles}
          notify={showSnackbar}
          cm={cm}
        />
      )}

      {tab === 2 && (
        <CardPermissionsTab
          profiles={profiles}
          reloadProfiles={loadProfiles}
          notify={showSnackbar}
          cm={cm}
        />
      )}

      {tab === 3 && (
        loading && agents.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : agents.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <MeetingRoomIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {cm('accessManagement.emptyTitle', 'No panel computer paired yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cm('accessManagement.emptyHint', 'Generate an 11-digit code from Device Management > Add Access Control Computer, then enter it in local-service under Options > Access Management.')}
            </Typography>
          </Paper>
        ) : (
          agents.map(renderAgent)
        )
      )}

      {/* Panel düzenleme */}
      <Dialog open={!!panelDialog} onClose={() => !savingPanel && setPanelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{cm('accessManagement.editPanel', 'Edit panel')}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={cm('accessManagement.panelName', 'Panel name')}
                value={panelForm.name}
                onChange={(e) => setPanelForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={cm('accessManagement.location', 'Location')}
                value={panelForm.location}
                onChange={(e) => setPanelForm((prev) => ({ ...prev, location: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{cm('accessManagement.branch', 'Branch')}</InputLabel>
                <Select
                  value={panelForm.branchId}
                  label={cm('accessManagement.branch', 'Branch')}
                  onChange={(e) => setPanelForm((prev) => ({ ...prev, branchId: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>{cm('accessManagement.noBranch', 'None')}</em>
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{cm('accessManagement.doorCount', 'Door count')}</InputLabel>
                <Select
                  value={panelForm.doorCount}
                  label={cm('accessManagement.doorCount', 'Door count')}
                  onChange={(e) => setPanelForm((prev) => ({ ...prev, doorCount: Number(e.target.value) }))}
                >
                  {[1, 2, 4].map((count) => (
                    <MenuItem key={count} value={count}>{count}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {cm('accessManagement.doorCountHint', 'Reducing the door count deactivates the surplus doors; it does not delete them.')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPanelDialog(null)} disabled={savingPanel}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={savePanel}
            disabled={savingPanel}
            startIcon={savingPanel ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {cm('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AccessManagement;
